// Serial numbers / IMEIs from an uploaded Excel (AI Studio, 2026-10-12).
//
// The AI reads the sheet for items, quantities and prices, but serial numbers
// are taken straight from the file here — exact, every one, and without
// sending long number lists through the AI.
//
// readSerialGroups(rows)  rows = sheet as an array of arrays of text
//   → { groups: [{ desc, code, serials: [] }], total } or null when the sheet
//     has no serial / IMEI column.
// attachSerials(lines, groups)
//   → { lines (with serialNumbers where matched), unmatched: number of serials
//       that could not be put on any line }

const SERIAL_HEAD = /(imei|serial|s\/n|\bsn\b|\besn\b|meid)/i;
// "S.No", "Sr #", "#" … are row numbers, not serials.
const ROWNO_HEAD = /^(#|s\.?\s*no\.?|sr\.?\s*(no\.?|#)?|sl\.?\s*no\.?|no\.?|row|serial\s*#?|serial\s*no\.?)$/i;
const DESC_HEAD = /(description|item\s*name|product\s*name|product|article|model)/i;
const CODE_HEAD = /(item\s*code|product\s*code|sku|part\s*no)/i;
const PARTY_HEAD = /(vendor|supplier|customer|party|account)/i;

const text = (v) => String(v ?? '').trim();
export const normText = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** One cell can hold several serials: comma, semicolon, | or new line. */
export const splitSerials = (cell) => text(cell).split(/[\s,;|]+/).map(s => s.trim()).filter(Boolean);

// A column of 1, 2, 3 … is a row-number column even if it is called "Serial No".
const looksLikeRowNumbers = (values) => {
  const nums = values.filter(Boolean).map(Number);
  if (nums.length < 2 || nums.some(n => !Number.isInteger(n))) return false;
  return nums.every((n, i) => i === 0 || n === nums[i - 1] + 1) && nums[0] <= 1 + 1;
};

export function readSerialGroups(rows) {
  if (!Array.isArray(rows) || rows.length < 2) return null;
  // Header = first of the top 10 rows that has a serial-like heading.
  let headIdx = -1;
  let serialCol = -1;
  for (let r = 0; r < Math.min(rows.length, 10) && headIdx < 0; r++) {
    const row = rows[r] || [];
    const candidates = row.map((h, c) => ({ h: text(h), c }))
      .filter(({ h }) => h && SERIAL_HEAD.test(h));
    for (const { h, c } of candidates) {
      const values = rows.slice(r + 1).map(x => text((x || [])[c]));
      if (ROWNO_HEAD.test(h) && looksLikeRowNumbers(values)) continue;
      if (/^(s\.?\s*no\.?|sr\.?\s*no\.?|sl\.?\s*no\.?|#)$/i.test(h)) continue;
      if (looksLikeRowNumbers(values)) continue;
      headIdx = r; serialCol = c; break;
    }
  }
  if (headIdx < 0) return null;
  const head = (rows[headIdx] || []).map(text);
  let descCol = head.findIndex((h, c) => c !== serialCol && /description/i.test(h));
  if (descCol < 0) descCol = head.findIndex((h, c) => c !== serialCol && DESC_HEAD.test(h));
  let codeCol = head.findIndex((h, c) => c !== serialCol && c !== descCol && CODE_HEAD.test(h));
  if (codeCol < 0) codeCol = head.findIndex((h, c) => c !== serialCol && c !== descCol && /code/i.test(h) && !PARTY_HEAD.test(h));

  const groups = [];
  const byKey = {};
  let total = 0;
  rows.slice(headIdx + 1).forEach(row => {
    const r = row || [];
    const serials = splitSerials(r[serialCol]);
    if (!serials.length) return;
    const desc = descCol >= 0 ? text(r[descCol]) : '';
    const code = codeCol >= 0 ? text(r[codeCol]) : '';
    const key = normText(desc) || normText(code) || '(none)';
    if (!byKey[key]) { byKey[key] = { desc, code, serials: [] }; groups.push(byKey[key]); }
    byKey[key].serials.push(...serials);
    total += serials.length;
  });
  return total ? { groups, total } : null;
}

export function attachSerials(lines, groups) {
  const used = new Set();
  const sumOf = (idxs) => idxs.reduce((s, gi) => s + groups[gi].serials.length, 0);
  const out = (lines || []).map(line => {
    const texts = [line.description, line.itemName].map(normText).filter(Boolean);
    const code = normText(line.itemCode);
    const qty = Number(line.quantity) || 0;
    const free = groups.map((g, gi) => gi).filter(gi => !used.has(gi));
    // 1. Same item description.
    let pick = free.filter(gi => texts.includes(normText(groups[gi].desc)));
    // 2. Same item code — only when it is not ambiguous (or the AI merged
    //    them into one line and the counts agree).
    if (!pick.length && code) {
      const byCode = free.filter(gi => normText(groups[gi].code) === code);
      if (byCode.length === 1 || (byCode.length > 1 && sumOf(byCode) === qty)) pick = byCode;
    }
    // 3. One description contains the other (counts must agree if several).
    if (!pick.length && texts.length) {
      const near = free.filter(gi => {
        const d = normText(groups[gi].desc);
        return d && texts.some(t => d.includes(t) || t.includes(d));
      });
      if (near.length === 1 || (near.length > 1 && sumOf(near) === qty)) pick = near;
    }
    if (!pick.length) return line;
    pick.forEach(gi => used.add(gi));
    return { ...line, serialNumbers: pick.flatMap(gi => groups[gi].serials) };
  });
  const unmatched = groups.reduce((s, g, gi) => s + (used.has(gi) ? 0 : g.serials.length), 0);
  return { lines: out, unmatched };
}
